import React, { useState } from "react";
import { toast } from "sonner";
import { NotebookPen, Check } from "lucide-react";
import { journalApi } from "../lib/journal";
import { useAuth } from "../lib/authContext";

/**
 * Inline journal card displayed at the bottom of Bible chapters and devotionals.
 *
 * Props:
 *   ref, short id used for grouping (e.g., "joao-3")
 *   refLabel, human label shown after save (e.g., "João 3, 04/05/2026")
 *   type, "bible" | "devotional"
 *   bookSlug, chapter, devotionalId, optional metadata
 *
 * Behavior:
 *   - Anonymous → saves to localStorage (cm_journal_v1)
 *   - Authenticated → POST /api/journal
 *   - Shows "✓ Reflexão salva" briefly after save
 */
export default function JournalPrompt({ refKey, refLabel, type, bookSlug, chapter, devotionalId }) {
  const { isAuthenticated } = useAuth();
  const [text, setText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  const save = async () => {
    const trimmed = text.trim();
    if (!trimmed) return;
    setSubmitting(true);
    try {
      await journalApi.create(isAuthenticated, {
        ref: refKey,
        refLabel,
        type,
        bookSlug: bookSlug || null,
        chapter: chapter || null,
        devotionalId: devotionalId || null,
        text: trimmed,
      });
      setSavedAt(new Date().toISOString());
      setText("");
      toast.success("Reflexão salva.", {
        description: isAuthenticated ? "Salva na sua conta." : "Salva neste dispositivo.",
      });
    } catch {
      toast.error("Não foi possível salvar agora.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      data-testid="journal-prompt"
      className="mt-10 rounded-2xl border border-gold/40 bg-[hsl(var(--parchment))] p-5 sm:p-6"
    >
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-white/70 flex items-center justify-center">
          <NotebookPen className="w-4 h-4 text-gold" />
        </div>
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-[0.3em] text-gold">Diário espiritual</p>
          <h3 className="font-display text-lg sm:text-xl text-navy mt-0.5 leading-tight">
            O que Deus falou com você hoje?
          </h3>
          {refLabel && (
            <p className="text-[11px] text-navy/55 mt-0.5">{refLabel}</p>
          )}
        </div>
      </div>

      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Escreva sua reflexão, oração ou insight..."
        rows={4}
        maxLength={3000}
        data-testid="journal-prompt-text"
        className="mt-4 w-full rounded-xl border border-border bg-white p-4 text-sm text-navy/90 leading-relaxed resize-y focus:outline-none focus:ring-2 focus:ring-gold/40"
      />

      <div className="mt-4 flex items-center justify-between gap-3">
        {savedAt ? (
          <p className="text-[11px] text-gold flex items-center gap-1.5" data-testid="journal-prompt-saved">
            <Check className="w-3.5 h-3.5" /> Reflexão salva
          </p>
        ) : (
          <p className="text-[11px] text-navy/45">
            {isAuthenticated ? "Salvo na sua conta." : "Salvo neste dispositivo."}
          </p>
        )}
        <button
          type="button"
          onClick={save}
          disabled={submitting || !text.trim()}
          data-testid="journal-prompt-save"
          className="inline-flex items-center gap-2 bg-gold text-navy hover:bg-navy hover:text-white disabled:opacity-50 px-5 py-2.5 rounded-full text-[11px] uppercase tracking-widest transition-colors"
        >
          {submitting ? "Salvando..." : "Salvar reflexão"}
        </button>
      </div>
    </section>
  );
}
